/**
 * Iter 150 — Page publique Prospection (Kamel)
 *
 * Lien direct : /restez-informe
 * Formulaire RGPD-compliant pour s'inscrire à la base RAAV et recevoir les
 * infos sur les futurs voyages.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProspectionPage = () => {
  const [form, setForm] = useState({
    prenom: '', nom: '', ville: '', code_postal: '',
    telephone: '', email: '', note: '', consent_rgpd: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.consent_rgpd) {
      setError('Merci de cocher la case de consentement RGPD pour continuer.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/prospects/register`, {
        prenom: form.prenom,
        nom: form.nom.toUpperCase(),
        ville: form.ville,
        code_postal: form.code_postal,
        telephone: form.telephone,
        email: form.email,
        note: form.note,
        consent_rgpd: form.consent_rgpd,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'envoi. Réessayez ou contactez-nous.");
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#e0f2fe] to-[#bae6fd] flex items-center justify-center p-4" data-testid="prospection-success">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="text-5xl mb-3">🌍</div>
          <h2 className="text-2xl font-bold text-[#1E3A5F] mb-2">Merci !</h2>
          <p className="text-gray-700 mb-4">
            Vous êtes inscrit·e dans notre base. Nous vous contacterons dès qu'un voyage qui vous correspond est lancé.
          </p>
          <p className="text-sm text-gray-500 italic mb-6">
            En attendant, découvrez nos voyages déjà ouverts.
          </p>
          <Link
            to="/voyages"
            className="inline-block bg-[#1E3A5F] hover:bg-[#2d5a8f] text-white px-6 py-3 rounded-xl font-bold shadow-lg transition"
            data-testid="prospection-success-cta"
          >
            🧳 Voir nos voyages
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#e0f2fe] to-[#bae6fd] py-12 px-4" data-testid="prospection-page">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-6">
          <Link to="/" className="text-[#1E3A5F] text-sm hover:underline">← Retour à l'accueil</Link>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
          <div className="text-center mb-6">
            <div className="text-5xl mb-3">📬</div>
            <h1 className="text-3xl font-bold text-[#1E3A5F] mb-2">Restez informé·e de nos futurs voyages</h1>
            <p className="text-gray-600 text-sm">
              Inscrivez-vous à notre base et recevez en avant-première les nouveaux voyages
              de Rhône Alpes Amitié Voyages.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-3 mb-4 text-sm text-red-800" data-testid="prospection-error">
              ❌ {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">Prénom *</label>
                <input type="text" required value={form.prenom}
                  onChange={(e) => setForm({ ...form, prenom: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm bg-white border-2 border-[#5BC5D4] focus:border-[#1E3A5F] focus:outline-none"
                  data-testid="prospection-prenom" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">NOM *</label>
                <input type="text" required value={form.nom}
                  onChange={(e) => setForm({ ...form, nom: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 rounded-lg text-sm bg-white border-2 border-[#5BC5D4] focus:border-[#1E3A5F] focus:outline-none"
                  data-testid="prospection-nom" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="text-xs font-bold text-gray-700 block mb-1">Ville</label>
                <input type="text" value={form.ville}
                  onChange={(e) => setForm({ ...form, ville: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm bg-white border-2 border-[#5BC5D4] focus:border-[#1E3A5F] focus:outline-none"
                  data-testid="prospection-ville" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">Code postal</label>
                <input type="text" value={form.code_postal} maxLength={10}
                  onChange={(e) => setForm({ ...form, code_postal: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm bg-white border-2 border-[#5BC5D4] focus:border-[#1E3A5F] focus:outline-none"
                  data-testid="prospection-cp" />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-gray-700 block mb-1">Téléphone *</label>
              <input type="tel" required value={form.telephone}
                onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                className="w-full px-3 py-2 rounded-lg text-sm bg-white border-2 border-[#5BC5D4] focus:border-[#1E3A5F] focus:outline-none"
                placeholder="06 12 34 56 78"
                data-testid="prospection-telephone" />
            </div>

            <div>
              <label className="text-xs font-bold text-gray-700 block mb-1">Email *</label>
              <input type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2 rounded-lg text-sm bg-white border-2 border-[#5BC5D4] focus:border-[#1E3A5F] focus:outline-none"
                placeholder="vous@exemple.com"
                data-testid="prospection-email" />
            </div>

            <div>
              <label className="text-xs font-bold text-gray-700 block mb-1">Note libre (facultatif)</label>
              <textarea rows={3} maxLength={500} value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="w-full px-3 py-2 rounded-lg text-sm bg-white border-2 border-[#5BC5D4] focus:border-[#1E3A5F] focus:outline-none resize-none"
                placeholder="Quel type de voyages vous intéresse ? Préférences ?…"
                data-testid="prospection-note" />
            </div>

            <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer pt-2">
              <input type="checkbox" required checked={form.consent_rgpd}
                onChange={(e) => setForm({ ...form, consent_rgpd: e.target.checked })}
                className="mt-0.5"
                data-testid="prospection-consent" />
              <span>
                J'accepte que <strong>Rhône Alpes Amitié Voyages</strong> conserve mes coordonnées dans sa base
                pour me tenir informé·e de ses futurs voyages. Mes données ne seront jamais transmises à des
                tiers. Je peux demander leur suppression à tout moment.
              </span>
            </label>

            <button type="submit" disabled={submitting}
              className={`w-full px-6 py-3 rounded-xl font-bold transition text-base ${submitting ? 'bg-gray-400 text-white cursor-wait' : 'bg-[#1E3A5F] hover:bg-[#2d5a8f] text-white shadow-lg hover:shadow-xl'}`}
              data-testid="prospection-submit">
              {submitting ? '⏳ Envoi en cours…' : '📬 Je m\'inscris à la base voyageurs'}
            </button>
          </form>
        </div>

        <p className="text-xs text-center text-gray-500 mt-6 italic">
          Vous restez maître de vos données : conformément au RGPD, vous pouvez nous demander à
          tout moment la suppression de vos informations.
        </p>
      </div>
    </div>
  );
};

export default ProspectionPage;
